/**
 * PSHWO-64518: leitura da telemetria por arte (camada de relatório).
 *
 * Backend (TH-55392 + PSHWO-64518):
 *   GET /custom-arts/{id}/stats
 *
 * Autorização é do backend (403 para quem não pode ver a arte):
 *   - super_admin: qualquer arte
 *   - tenant_admin/operator: só artes do próprio tenant (scope=tenant)
 *   - catalog_operator: artes de fornecedor (scope=supplier)
 *
 * A UI trata erro como "sem dado" (traço) em vez de quebrar o card — a
 * métrica é acessória à ação principal (curadoria / catálogo).
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

/** Um dia da série de 30 dias — sempre preenchido (zero quando sem evento). */
export interface ArtDailyPoint {
  /** Data ISO (YYYY-MM-DD). */
  day: string;
  views: number;
  uses: number;
}

export interface ArtStatsRead {
  art_id: string;
  use_count: number;
  view_count: number;
  unique_tenants_viewed: number;
  unique_tenants_used: number;
  last_7_days_views: number;
  last_7_days_uses: number;
  /** Série dos últimos 30 dias, do mais antigo para o mais recente. */
  daily_30d: ArtDailyPoint[];
}

async function fetchArtStats(artId: string): Promise<ArtStatsRead> {
  const res = await fetch(`/api/custom-arts/${artId}/stats`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ArtStatsRead;
}

/**
 * Estatísticas de uma arte (views/usos + série 30d).
 *
 * @param artId - ID da arte; query fica desabilitada quando vazio.
 * @param enabled - Desliga a chamada (ex.: role sem acesso ao relatório).
 */
export function useArtStats(
  artId: string,
  enabled = true,
): UseQueryResult<ArtStatsRead, Error> {
  return useQuery({
    queryKey: ["art-stats", artId],
    queryFn: () => fetchArtStats(artId),
    enabled: enabled && !!artId,
    // Telemetria não precisa ser fresca a cada foco: 1 min basta e evita
    // refetch em rajada quando a galeria tem dezenas de cards.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
